// === Common ===

export interface PracticeResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// === Status ===

export type PracticeStatus =
  | "not_available"
  | "not_started"
  | "ongoing"
  | "finished";

export interface PracticeStatusData {
  user_id: string;
  status: PracticeStatus;
}

// === Session ===

export enum PracticeSessionQuestionStatus {
  InProgress,
  Correct,
  Incorrect
}

export type PracticeButtonState =
  | "TakePhoto"
  | "TryAgain"
  | "AnswerWithMcq"
  | "AnswerMcq"
  | "NextQuestion"
  | "Done";

export interface PracticeSessionData {
  id: string;
  number_of_questions: number;
  current_question_number: number;
  current_question: PracticeQuestion;
}

export interface PracticeQuestion {
  id: string;
  title: string;
  topic: string;
  hint: string;
  assets: string[];
  answers: PracticeAnswer[];
  attempts: PracticeAttempt[];
  max_points: number;
}

export interface PracticeAnswer {
  id: string;
  title: string;
  is_correct: boolean;
  assets: string[];
}

export interface PracticeAttempt {
  id: string;
  attempt_number: number;
  is_correct: boolean;
  attempt_type: "ai" | "mcq";
  mcq_selected_answer_id: string | null;
  ai_analysis: string | null;
  ai_answer_image_url: string | null;
  ai_analysis_voice_url: string | null;
  metadata: Record<string, unknown>;
}

// === Attempt Response ===

export interface PracticeAttemptResultData {
  point: number;
  is_correct: boolean;
  is_legible: boolean;
  is_relevant: boolean;
}

export interface PracticeAttemptRequest {
  practice_session_id: string;
  question_id: string;
  attempt_type: "ai" | "mcq";
  platform: "web";
  ai_image_key: string | null;
  mcq_selected_answer_id: string | null;
}

// === Presigned URL ===

export interface PracticePresignedUrlData {
  presigned_urls: string[];
  image_keys: string[];
  file_type: string;
  format: string;
  processed_at: string;
  record_id: string;
}

export interface PracticePresignedUrlParams {
  file_type: string;
  count: number;
  format: string;
}

// `fetch` accepts Blob and ArrayBuffer directly. Data URLs are also supported
// and converted to a Blob before uploading.
export type PracticeImagePayload = Blob | ArrayBuffer | string;

// === Leaderboard ===

export type PracticeLeaderboardType = "weekly" | "semesterly";

export interface PracticeLeaderboardItem {
  name: string;
  point: number;
}

export interface PracticeLeaderboardData {
  start_date: string;
  end_date: string;
  grade: number;
  my_position: number | null;
  leaderboard: PracticeLeaderboardItem[];
}

// === Points ===

export interface PracticePointsData {
  total_points: number;
  effort_points: number;
  correct_answer_points: number;
}

// === History ===

export interface PracticeHistoryData {
  id: string;
  start_date: string;
  end_date: string;
}

export interface PracticeHistoryDetailData {
  id: string;
  start_date: string;
  end_date: string;
  questions: PracticeQuestion[];
}
